import { useState } from "react";
import { Loader2, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { forceSignOut } from "@/lib/forceSignOut";

export interface BackendUnreachableProps {
  /** Re-runs the checks that failed. */
  onRetry: () => Promise<void> | void;
}

/**
 * Shown when the app cannot reach the backend to work out what the signed-in
 * user may see.
 *
 * The alternative — which is what shipped before — was to guess: an
 * unreachable server read as "no subscription", so a producer got the upgrade
 * page during an outage, and then a spinner that no refresh could clear. Say
 * plainly that it's the connection, and offer the one action that helps.
 */
export function BackendUnreachable({ onRetry }: BackendUnreachableProps) {
  const [isRetrying, setIsRetrying] = useState(false);

  const signOut = async () => {
    // Not a plain supabase signOut: that one posts to the server first and
    // keeps the tokens when the post fails — which is exactly the case here.
    await forceSignOut();
    window.location.assign("/");
  };

  const retry = async () => {
    setIsRetrying(true);
    try {
      await onRetry();
    } catch {
      // Still down. This screen stays up and the button becomes clickable
      // again — a rejected retry must not surface as an unhandled error.
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-8 text-center">
        <WifiOff className="mx-auto mb-4 h-10 w-10 text-muted-foreground" aria-hidden />
        <h1 className="mb-2 text-xl font-semibold">No connection to the server</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          We couldn&apos;t load your account details. This is a connection problem, not a change to
          your subscription — your productions are safe.
        </p>
        <Button onClick={retry} disabled={isRetrying} className="w-full">
          {isRetrying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isRetrying ? "Trying again…" : "Try again"}
        </Button>
        {/* A way out. Without it a persistent failure (not just a passing
            outage) traps the user on this screen with nowhere to go. */}
        <Button variant="ghost" onClick={signOut} className="mt-2 w-full">
          Sign out
        </Button>
      </div>
    </div>
  );
}
